import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useClientProject } from '@/contexts'
import { LeaseIntelligenceDashboard } from '@/components/lease-intelligence'
import { EmploymentIntelligenceDashboard } from '@/components/employment-intelligence'
import { ClientIntelligenceDashboard } from '@/components/client-intelligence'
import { SemanticDashboard } from '@/components/charts/SemanticDashboard'
import { DealExportButton } from '@/components/export/DealExportButton'
import { CONTRACT_ESTATE } from '@/config/dashboards/contractEstate'
import { CLIENT_INTELLIGENCE } from '@/config/dashboards/clientIntelligence'
import { EMPLOYMENT_INTELLIGENCE } from '@/config/dashboards/employmentIntelligence'
import '@/styles/pages.css'
import '@/styles/dashboard-subnav.css'

// 'supplier' is intentionally omitted: Suppliers is now a locked teaser (no real view), so a
// ?section=supplier URL coerces to undefined and falls back to the default section rather than
// rendering an empty "coming soon" heading. 'client-compare'/'contract-estate' stay valid (their
// render branches are kept) even though their nav buttons are hidden.
const VALID_SECTIONS = ['portfolio-overview', 'client-compare', 'contract-estate', 'employment', 'employment-semantic', 'leases'] as const
type SectionId = typeof VALID_SECTIONS[number]

interface OverviewSearch {
  section?: SectionId
  tab?: string
  /** The Clients suite's ZDR Review filter (`dimension:value;…`), kept across opening a family (AER-4 REQ-6). */
  zdr?: string
}

export const Route = createFileRoute('/_app/organization/overview')({
  head: () => ({ meta: [{ title: 'Overview · AERRIS' }] }),
  component: OrganizationOverviewPage,
  validateSearch: (search: Record<string, unknown>): OverviewSearch => {
    const s = search.section
    return {
      section: typeof s === 'string' && (VALID_SECTIONS as readonly string[]).includes(s)
        ? (s as SectionId)
        : undefined,
      tab: typeof search.tab === 'string' ? search.tab : undefined,
      zdr: typeof search.zdr === 'string' && search.zdr !== '' ? search.zdr : undefined,
    }
  },
})

// Compare and Contract Estate are intentionally hidden from the sub-nav (their
// render branches + routes below stay intact, so a direct ?section= URL still works).
const dashboardSections: { id: SectionId; label: string }[] = [
  { id: 'portfolio-overview', label: 'Clients' },
  { id: 'employment', label: 'Employment' },
  // The semantic (config-driven) replica runs alongside the bespoke Employment dashboard until
  // it proves itself end-to-end (gaps ledger #11); then one of them goes.
  { id: 'employment-semantic', label: 'Employment (Semantic)' },
  { id: 'leases', label: 'Leases' },
]

// Suppliers lives here (greyed teaser) until it ships — moved out of the clickable
// sections above so a dead "coming soon" click is replaced by an honest locked module.
const lockedSections = [
  'Suppliers',
  'Corporate Structure',
  'Financing & Securities',
  'Intellectual Property rights',
  'Insurance',
  'Disputes & Litigation',
  'Data Protection & Privacy',
  'Environment, health and safety',
  'Regulatory & Licensing',
  'Tax',
]

/** Compile-time proof that the section switch below covers every `SectionId`. */
function assertNever(section: never): never {
  throw new Error(`Unhandled overview section: ${String(section)}`)
}

function OrganizationOverviewPage() {
  const { projectId, selectedProject } = useClientProject()
  const navigate = useNavigate()
  const { section, tab, zdr } = Route.useSearch()
  const [otherExpanded, setOtherExpanded] = useState(true)
  const activeSection: SectionId = section ?? 'portfolio-overview'
  const setActiveSection = (next: SectionId) => {
    navigate({
      to: '/organization/overview',
      // Preserve clientId/projectId (stored in URL search by ClientProjectContext);
      // only reset the per-section `tab` since it's section-specific.
      search: (prev) => ({ ...prev, section: next, tab: undefined, zdr: undefined }),
    })
  }
  const setTab = (next: string) => {
    navigate({
      to: '/organization/overview',
      search: (prev) => ({ ...prev, tab: next }),
    })
  }
  const setZdrFilter = (next: string | undefined) => {
    navigate({
      to: '/organization/overview',
      search: (prev) => ({ ...prev, zdr: next }),
      replace: true,
    })
  }

  return (
    <div className="dashboard-page">
      {/* Dashboard sub-nav — vertical left sidebar navigation for contract areas */}
      <aside className="dashboard-subnav">
        {dashboardSections.map((section) => (
          <button
            key={section.id}
            className={`dashboard-subnav-item ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            {section.label}
          </button>
        ))}

        <button
          className="dashboard-subnav-divider"
          onClick={() => setOtherExpanded((v) => !v)}
          aria-expanded={otherExpanded}
        >
          <span>— Other Due Diligence Modules —</span>
          <span className={`dashboard-subnav-chevron ${otherExpanded ? 'open' : ''}`}>▾</span>
        </button>

        {otherExpanded && lockedSections.map((label) => (
          <div key={label} className="dashboard-subnav-item locked" title="Contact us to unlock insights for further legal areas">
            {label}
          </div>
        ))}
      </aside>

      <div className="dashboard-content">
        {projectId && (
          <div className="dashboard-toolbar">
            <DealExportButton projectId={projectId} projectName={selectedProject?.name} />
          </div>
        )}
        {projectId ? (
          activeSection === 'leases' ? (
            <LeaseIntelligenceDashboard projectId={projectId} tab={tab} onTabChange={setTab} />
          ) : activeSection === 'employment' ? (
            <EmploymentIntelligenceDashboard projectId={projectId} tab={tab} onTabChange={setTab} />
          ) : activeSection === 'employment-semantic' ? (
            <SemanticDashboard projectUuid={projectId} definitions={EMPLOYMENT_INTELLIGENCE} tab={tab} onTabChange={setTab} />
          ) : activeSection === 'portfolio-overview' ? (
            <ClientIntelligenceDashboard
              projectId={projectId}
              tab={tab}
              onTabChange={setTab}
              zdrFilter={zdr}
              onZdrFilterChange={setZdrFilter}
            />
          ) : activeSection === 'client-compare' ? (
            <div className="dashboard-compare">
              <section className="dashboard-compare-col">
                <header className="dashboard-compare-head">Bespoke dashboard (REST)</header>
                <ClientIntelligenceDashboard projectId={projectId} />
              </section>
              <section className="dashboard-compare-col">
                <header className="dashboard-compare-head">Config-driven semantic (REST · beta)</header>
                <SemanticDashboard projectUuid={projectId} definitions={CLIENT_INTELLIGENCE} />
              </section>
            </div>
          ) : activeSection === 'contract-estate' ? (
            <SemanticDashboard projectUuid={projectId} definitions={CONTRACT_ESTATE} />
          ) : (
            // C.4 rail audit: every VALID_SECTION above renders something, so the old "…dashboards
            // coming soon" teaser here was unreachable (and printed "undefined dashboards coming
            // soon" for the two hidden sections). `assertNever` keeps that true — adding a section
            // id without a branch is now a compile error, not a blank panel.
            assertNever(activeSection)
          )
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '200px',
            color: 'var(--text-muted, #6b7280)',
            textAlign: 'center',
            backgroundColor: 'var(--container-bg, #ffffff)',
            border: '1px solid var(--border-color, #e5e7eb)',
            borderRadius: 'var(--radius-lg)'
          }}>
            <h2 style={{ fontSize: '1.125rem', margin: 0 }}>Select a Client and Project</h2>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>Choose a client and project to view the dashboard</p>
          </div>
        )}
      </div>
    </div>
  )
}
